import { isYahooPlatform } from "../utils/platformPresentationOrder";
import { formatMetricCountOrRange } from "../utils/micrositeMetricFormat";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import "./SherlocMediaPlanCharts.css";
import {
  normalizePlatformAllocationKey,
  orderPlatformRowsByAllocation,
  resolveCanonicalChartPlatformKey,
  parseSherlocBudgetAllocationItems,
  resolvePlatformAllocationPercent,
  resolvePlatformBudgetAmount,
} from "../utils/mediaPlanAllocations";
import {
  PLATFORM_FALLBACK_BRANDING,
  resolveMicrositePlatformBranding,
  resolveSherlocPlatformBranding,
} from "../utils/platformBranding";
import { resolveBenchmarkUnit } from "../utils/micrositeChannelProjection";

const FALLBACK_COLORS = ["#2f6fff", "#39c5a5", "#8f63e8", "#f39a34", "#df5f7a", "#42c8f5", "#f0c04a", "#55d07f"];

const STAGE_COLORS = {
  Awareness: "#2f6fff",
  "Consideration / Engagement": "#8f63e8",
  "Conversion + Ticketing": "#39c5a5",
  Retargeting: "#df5f7a",
};

const POPOVER_SIZE = { width: 260, height: 188 };
const POPOVER_GAP = 12;
const POPOVER_VIEWPORT_PADDING = 12;

const AWARENESS_SIGNALS = [
  "awareness",
  "reach",
  "video views",
  "completed views",
  "trailer distribution",
  "prospecting",
  "upper funnel",
  "upper-funnel",
  "top funnel",
  "top-funnel",
  "brand awareness",
  "theatrical awareness",
  "audience growth",
  "audience buildup",
  "broad audience",
  "premium awareness",
  "market-level awareness",
  "ctv",
  "connected tv",
  "dooh",
  "digital out of home",
];

const CONSIDERATION_SIGNALS = [
  "consideration",
  "engagement",
  "traffic",
  "site visits",
  "trailer viewers",
  "trailer views",
  "social engagement",
  "click intent",
  "landing page visits",
  "view-through",
  "discovery",
  "viral traction",
];

const RETARGETING_SIGNALS = [
  "retargeting",
  "remarketing",
  "highest roi",
  "re-engagement",
  "warm audience",
  "cart abandon",
  "site retargeting",
  "sequential retargeting",
  "reminder",
];

const CONVERSION_SIGNALS = [
  "conversion",
  "ticketing",
  "ticket purchase",
  "purchase intent",
  "purchase-intent",
  "intent-based ticketing",
  "showtimes",
  "theater locations",
  "demand capture",
  "ticket sales",
  "lower funnel",
  "lower-funnel",
];

const normalizeKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");

export function parseBudgetValue(value) {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const raw = String(value ?? "").trim();
  if (!raw || /^(n\/a|null|undefined|nan)$/i.test(raw)) return null;
  const multiplier = /k/i.test(raw) ? 1000 : /m/i.test(raw) ? 1000000 : 1;
  const cleaned = raw.replace(/[$,%]/g, "").replace(/,/g, "").replace(/[^\d.-]/g, "");
  const numeric = Number(cleaned);
  return Number.isFinite(numeric) ? numeric * multiplier : null;
}

const parsePercentValue = (value) => {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const match = String(value ?? "").match(/-?\d+(?:\.\d+)?/);
  if (!match) return null;
  const numeric = Number(match[0]);
  return Number.isFinite(numeric) ? numeric : null;
};

const firstPresent = (...values) => {
  for (const value of values) {
    if (value === undefined || value === null) continue;
    if (typeof value === "string" && !value.trim()) continue;
    return value;
  }
  return null;
};

const asArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object") return Object.values(value);
  return [];
};

const asObject = (value) =>
  value && typeof value === "object" && !Array.isArray(value) ? value : {};

const getPlatformMatchKey = (row) => resolveCanonicalChartPlatformKey(row);

const parseRateValue = (value) => {
  if (typeof value === "number") return Number.isFinite(value) && value > 0 ? value : null;
  const raw = String(value ?? "").trim();
  if (!raw || /^(n\/a|null|undefined|nan)$/i.test(raw)) return null;
  const matches = raw.match(/\d+(?:,\d{3})*(?:\.\d+)?|\d+(?:\.\d+)?/g);
  if (!matches?.length) return null;
  const values = matches
    .map((match) => Number(match.replace(/,/g, "")))
    .filter((number) => Number.isFinite(number) && number > 0);
  if (!values.length) return null;
  return values.reduce((sum, number) => sum + number, 0) / values.length;
};

// Parse a delivery count (impressions/clicks) that may arrive as a number,
// a suffixed string ("5M", "281K"), or a range ("281K–422K"). Unlike
// parseBudgetValue this keeps each token's own K/M/B suffix and averages a
// range, so "281K–422K" resolves to ~351,500 rather than 281,422,000.
const parseDeliveryCountValue = (value) => {
  if (typeof value === "number") return Number.isFinite(value) && value > 0 ? value : null;
  const raw = String(value ?? "").trim();
  if (!raw || /^(n\/a|null|undefined|nan)$/i.test(raw)) return null;
  const tokens = raw.match(/\d+(?:,\d{3})*(?:\.\d+)?\s*[kmb]?/gi);
  if (!tokens?.length) return null;
  const values = tokens
    .map((token) => {
      const numeric = Number(token.replace(/,/g, "").replace(/[kmb]/i, "").trim());
      if (!Number.isFinite(numeric)) return null;
      const suffix = token.match(/[kmb]/i)?.[0]?.toLowerCase();
      const multiplier = suffix === "b" ? 1e9 : suffix === "m" ? 1e6 : suffix === "k" ? 1e3 : 1;
      return numeric * multiplier;
    })
    .filter((number) => Number.isFinite(number) && number > 0);
  if (!values.length) return null;
  return values.reduce((sum, number) => sum + number, 0) / values.length;
};

// Choose the estimated impressions to display. Incoming payload values are kept
// when they are roughly consistent with the resolved budget + CPM, but a stale
// or mis-scaled incoming value (off by an order of magnitude, or implying a
// sub-$1,000 budget when the resolved budget is five figures) is discarded in
// favor of the formula so the table never shows ~1,861 impressions for a ~$40K
// platform. Falls back to a delivery estimate only when no formula is available.
const resolveEstimatedDeliveryCount = ({ incoming, formula, fallback, budget, cpm }) => {
  const formulaValue = Number.isFinite(formula) && formula > 0 ? formula : null;
  const hasIncoming = !(incoming == null || (typeof incoming === "string" && !incoming.trim()));
  if (hasIncoming && formulaValue) {
    const incomingValue = parseDeliveryCountValue(incoming);
    if (Number.isFinite(incomingValue) && incomingValue > 0) {
      const ratio = formulaValue / incomingValue;
      const impliedBudget = cpm ? (incomingValue / 1000) * cpm : null;
      const budgetConflict = budget > 1000 && impliedBudget != null && impliedBudget < 1000;
      if (ratio >= 10 || ratio <= 0.1 || budgetConflict) return formulaValue;
    }
  }
  if (hasIncoming) return incoming;
  /* A published delivery figure that IS this formula held to display
     precision (three significant figures: within 0.5%) is the same estimate,
     so the chart shows the figure the Impression Outlook shows. One that
     disagrees further is stale against the resolved budget; the formula wins. */
  const fallbackValue = parseDeliveryCountValue(fallback);
  if (
    formulaValue &&
    Number.isFinite(fallbackValue) &&
    fallbackValue > 0 &&
    Math.abs(formulaValue - fallbackValue) <= 0.005 * Math.max(formulaValue, fallbackValue)
  ) {
    return fallback;
  }
  return firstPresent(formulaValue, fallback);
};

const isBenchmarkSourceOnlyLabel = (value) => {
  const text = String(value ?? "").trim();
  if (!text) return false;
  if (parseRateValue(text) != null) return false;
  if (/^(?:CPM|CPC|CPP)$/i.test(text)) return true;
  return /\bbenchmark\b|\bsource\b|\bspend\b|\bcontract\b|\bplanning\b|\bdirectional\b/i.test(text);
};

const firstBenchmarkRate = (...values) =>
  values.find((value) => firstPresent(value) && !isBenchmarkSourceOnlyLabel(value));

const getBenchmarkRateLabel = (row) =>
  firstBenchmarkRate(
    row?.primaryValue,
    row?.benchmark,
    row?.benchmarkValue,
    row?.rate,
    row?.benchmarkCpm,
    row?.benchmarkCPM,
    row?.billedCpm,
    row?.billedCPM,
    row?.cpmRange,
    row?.cpm,
    row?.estimatedCpm,
    row?.estCpm,
    row?.benchmarkCpc,
    row?.benchmarkCPC,
    row?.billedCpc,
    row?.billedCPC,
    row?.cpcRange,
    row?.cpc,
    row?.estimatedCpc,
    row?.estCpc,
    row?.benchmarkCpp,
    row?.benchmarkCPP,
    row?.billedCpp,
    row?.billedCPP,
    row?.cppRange,
    row?.cpp,
    row?.value
  );

const normalizeBenchmarkSourceLabel = (value) => {
  const text = String(value ?? "").trim();
  if (!text) return null;
  /* A backend-authored basis label may quote a figure ("Approved forecast ·
     client-billed · $35.00 net CPM"); that is a label, not a rate. */
  if (/^Approved forecast/i.test(text)) return text;
  if (parseRateValue(text) != null) return null;
  /* Underscored source keys read as words; hyphenated labels ("client-billed") are kept verbatim. */
  const normalized = text.replace(/_+/g, " ").replace(/\s+/g, " ").trim();
  if (!normalized) return null;
  if (/^(?:configured|planned pricing|contract)$/i.test(normalized)) return "Contract benchmark";
  if (/^(?:directional|planning|target margin|target margin benchmark)$/i.test(normalized)) return "Planning benchmark";
  if (/^planning benchmark$/i.test(normalized)) return "Planning benchmark";
  if (/^contract benchmark$/i.test(normalized)) return "Contract benchmark";
  return normalized;
};

const getBenchmarkSourceLabel = (row) => {
  const candidates = [
    row?.benchmarkSourceLabel,
    row?.benchmarkDisplayLabel,
    row?.benchmarkLabel,
    row?.sourceLabel,
    row?.secondaryValue,
    row?.benchmarkCpmLabel,
    row?.benchmarkCpcLabel,
    row?.benchmarkCppLabel,
    row?.cpmSourceLabel,
    row?.cpcSourceLabel,
    row?.cppSourceLabel,
    row?.cpmSource,
    row?.benchmarkCpmSource,
    row?.cpcSource,
    row?.benchmarkCpcSource,
    row?.cppSource,
    row?.benchmarkCppSource,
  ];
  for (const candidate of candidates) {
    const label = normalizeBenchmarkSourceLabel(candidate);
    if (label) return label;
  }
  return null;
};

const getBenchmarkMetricType = (row, key) => {
  const descriptor = [
    row?.metricType,
    row?.metric,
    row?.unit,
    row?.pricingModel,
    row?.secondaryValue,
    row?.subtitle,
    row?.label,
    row?.title,
  ]
    .filter(Boolean)
    .join(" ");
  // The unit carried by the actual rate value (e.g. "$23.40 CPM") is
  // authoritative and must beat platform-role guesses like "paid search" — a
  // CPM benchmark must never be routed into the CPC bucket (that is the
  // "641 clicks" bug). resolveBenchmarkUnit applies that precedence; the
  // media-plan table only renders CPM/CPC/CPP buckets, so anything else falls
  // back to the platform default.
  const unit = resolveBenchmarkUnit({
    rateText: getBenchmarkRateLabel(row),
    descriptor,
    cpmFieldPresent:
      firstPresent(row?.benchmarkCpm, row?.benchmarkCPM, row?.billedCpm, row?.billedCPM, row?.cpmRange, row?.cpm, row?.estimatedCpm, row?.estCpm) != null,
    cpcFieldPresent:
      firstPresent(row?.benchmarkCpc, row?.benchmarkCPC, row?.billedCpc, row?.billedCPC, row?.cpcRange, row?.cpc, row?.estimatedCpc, row?.estCpc) != null,
    cppFieldPresent:
      firstPresent(row?.benchmarkCpp, row?.benchmarkCPP, row?.billedCpp, row?.billedCPP, row?.cppRange, row?.cpp) != null,
    platformKey: key,
  });
  if (unit === "CPM" || unit === "CPC" || unit === "CPP") return unit;
  // No explicit unit signal: default to CPM (impressions). Search platforms are
  // NOT hard-coded to CPC — a bare planning-benchmark number on Google Search is
  // a CPM like every other platform (the "641 clicks" bug). Genuine CPC always
  // carries an explicit CPC token / metricType / cpc-typed field above.
  return "CPM";
};

const PLATFORM_PRIMARY_OBJECTIVE_FALLBACKS = Object.freeze({
  youtube: "Trailer views, long-form engagement, and consideration.",
  meta: "Engagement, social amplification, and retargeting.",
  ctv: "Awareness, prestige reach, and trailer completion.",
  google_search: "Conversion and ticket intent.",
  display: "Retargeting and conversion reinforcement.",
  tiktok: "Viral reach and short-form engagement.",
  dooh: "Awareness and theater reinforcement.",
  mobile: "Cross-device reinforcement and geo retargeting.",
  audio: "Frequency reinforcement and awareness.",
  reddit: "Community interest validation.",
  amazon: "Commerce and shopper signal support.",
  shopify: "Commerce intelligence and conversion planning layer.",
  linkedin: "B2B and professional audience paid media.",
  truth_social: "Community amplification and conversation engagement.",
  rumble: "Community amplification and conversation engagement.",
  x: "Community amplification and conversation engagement.",
});

const CHART_PLATFORM_ORDER = Object.freeze([
  "meta",
  "youtube",
  "ctv",
  "tiktok",
  "x",
  "google_search",
  "rumble",
  "truth_social",
  "reddit",
  "amazon",
  "yahoo",
  "shopify",
  "linkedin",
  "video",
  "display",
  "mobile",
  "audio",
  "ingame",
  "dooh",
  "email",
]);

const CHART_PLATFORM_ORDER_INDEX = new Map(
  CHART_PLATFORM_ORDER.map((key, index) => [key, index])
);

const getChartPlatformOrderIndex = (row) => {
  const rawKey = getPlatformMatchKey(row);
  const key = isYahooPlatform(rawKey) ? "yahoo" : rawKey;
  return CHART_PLATFORM_ORDER_INDEX.has(key)
    ? CHART_PLATFORM_ORDER_INDEX.get(key)
    : Number.MAX_SAFE_INTEGER;
};

const compareChartPlatformRows = (a, b) => {
  const orderDelta = getChartPlatformOrderIndex(a) - getChartPlatformOrderIndex(b);
  if (orderDelta !== 0) return orderDelta;
  const budgetDelta = Number(b?.budget || 0) - Number(a?.budget || 0);
  if (budgetDelta !== 0) return budgetDelta;
  return String(a?.name || "").localeCompare(String(b?.name || ""));
};

function isGenericPrimaryObjective(value) {
  const normalized = String(value || "")
    .replace(/^(?:[-*]\s*)?primary\s+role\s*:\s*/i, "")
    .replace(/[^\w\s]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
  if (!normalized) return false;
  return (
    normalized === "awareness engagement ticket sales" ||
    (/^awareness engagement (and )?ticket sales$/.test(normalized)) ||
    (normalized.includes("awareness") &&
      normalized.includes("engagement") &&
      normalized.includes("ticket sales") &&
      normalized.split(" ").length <= 6)
  );
}

/* Presentation contract v2, retargeting not active: the fallback roles that
   named retargeting say what the lane does without it. Only read when the
   payload says the plan activates none. */
const PLATFORM_PRIMARY_OBJECTIVE_FALLBACKS_WITHOUT_RETARGETING = Object.freeze({
  meta: "Engagement and social amplification.",
  display: "Consideration and frequency continuity.",
  mobile: "Cross-device reinforcement.",
});

function getPlatformSpecificObjective(platform, { retargetingActive = undefined } = {}) {
  const key = getPlatformMatchKey(platform);
  if (retargetingActive === false && PLATFORM_PRIMARY_OBJECTIVE_FALLBACKS_WITHOUT_RETARGETING[key]) {
    return PLATFORM_PRIMARY_OBJECTIVE_FALLBACKS_WITHOUT_RETARGETING[key];
  }
  return PLATFORM_PRIMARY_OBJECTIVE_FALLBACKS[key] || null;
}

/*
 * The backend's presentation contract (campaign_microsite_presentation_v2) and
 * its audience-activation verdict ride the payload of a stamped artifact only.
 * A legacy payload (campaign 53's) carries neither field, so every gate reads
 * `undefined` / `false` and the chart renders exactly as it always has.
 *   canonicalDeliveryFigures: the channelSplit display values (CPM, impressions)
 *     are THE figures; the chart never re-derives a volume from a rounded CPM.
 *   retargetingActive === false: no stage, role or label presents retargeting.
 */
function resolvePresentationGates(plan = {}) {
  const safePlan = asObject(plan);
  const campaign = asObject(safePlan.campaign);
  const contract = asObject(safePlan.presentationContract || campaign.presentationContract);
  const features = asObject(contract.features);
  const activation = asObject(safePlan.audienceActivation || campaign.audienceActivation);
  return {
    canonicalDeliveryFigures: features.canonicalDeliveryFigures === true,
    retargetingActive:
      features.retargetingSuppressedWithoutAuthority === true && activation.retargetingActive === false
        ? false
        : undefined,
  };
}

function resolvePlatformPrimaryObjective(row = {}, metadata = {}, gates = {}) {
  const candidates = [
    row?.primaryObjective,
    row?.objective,
    row?.role,
    metadata?.primaryObjective,
    metadata?.objective,
    metadata?.primaryKpi,
    metadata?.role,
    metadata?.funnelPurpose,
  ];
  const specific = candidates.find((value) => firstPresent(value) && !isGenericPrimaryObjective(value));
  if (specific) return String(specific);
  return getPlatformSpecificObjective(row, gates) || candidates.find((value) => firstPresent(value)) || null;
}

const normalizeBenchmarkRow = (row) => {
  const key = getPlatformMatchKey(row);
  if (!key) return null;
  const label = getBenchmarkRateLabel(row);
  const sourceLabel = getBenchmarkSourceLabel(row);
  if (!label && !sourceLabel) return null;
  const metricType = getBenchmarkMetricType(row, key);
  return {
    key,
    benchmarkSourceLabel: sourceLabel,
    cppRange: metricType === "CPP" ? label : null,
    cpcRange: metricType === "CPC" ? label : null,
    cpmRange: metricType === "CPM" ? label : null,
  };
};

const normalizeDeliveryRow = (row) => {
  const key = getPlatformMatchKey(row);
  if (!key) return null;
  return {
    key,
    /*
     * The backend publishes each estimate at the precision its evidence
     * supports (`displayValue`: a directional benchmark volume held to three
     * significant figures). That published figure is the canonical one — the
     * microsite's Impression Outlook and Funnel Architecture show it too, so
     * the chart never prints 311,102 beside their 311,000.
     */
    estimatedImpressions: firstPresent(
      row?.displayValue,
      row?.estimatedImpressions,
      row?.impressions,
      row?.estimated_impressions,
      row?.value
    ),
    estimatedClicks: firstPresent(row?.estimatedClicks, row?.clicks, row?.estimated_clicks),
  };
};

function getStrategyAllocationRows(plan = {}) {
  return parseSherlocBudgetAllocationItems(
    firstPresent(plan?.strategy?.text, plan?.strategyText, plan?.text, plan?.analysisText)
  );
}

function getRequestedAllocationRows(plan = {}) {
  const safePlan = asObject(plan);
  const sections = asObject(safePlan.sections);
  const budget = asObject(safePlan.budgetArchitecture || sections.budgetArchitecture);
  const chartReady = asObject(safePlan.chartReady);
  const financials = asObject(safePlan.financials || safePlan.financialSummary || safePlan.financial_summary);
  const candidates = [
    safePlan.requestedChannelAllocation,
    safePlan.requested_channel_allocation,
    financials.requestedChannelAllocation,
    financials.requested_channel_allocation,
    budget.requestedChannelAllocation,
    budget.requested_channel_allocation,
    chartReady.requestedChannelAllocation,
    chartReady.requested_channel_allocation,
  ];
  return asArray(candidates.find((entry) => asArray(entry).length));
}

function isEstimatedEqualSplitFallback(rows = []) {
  const list = asArray(rows);
  if (list.length < 2) return false;
  const allEstimated = list.every((row) => row?.estimated || row?.isEstimatedFallback);
  if (!allEstimated) return false;
  const values = list
    .map((row) =>
      parsePercentValue(firstPresent(row?.percent, row?.share, row?.shareLabel, row?.allocation)) ??
      parseBudgetValue(firstPresent(row?.budget, row?.budgetLabel, row?.value))
    )
    .filter((value) => Number.isFinite(value));
  return values.length === list.length && new Set(values.map((value) => Math.round(value * 100))).size === 1;
}

function getCandidateRows(plan = {}) {
  const safePlan = asObject(plan);
  const sections = asObject(safePlan.sections);
  const budget = asObject(safePlan.budgetArchitecture);
  const sectionBudget = asObject(sections.budgetArchitecture);
  const chartReady = asObject(safePlan.chartReady);
  const requestedRows = getRequestedAllocationRows(safePlan);
  if (requestedRows.length) {
    return { rows: requestedRows, source: "requested_channel_allocation" };
  }

  const structuredRows = [
    budget.channelSplit,
    sectionBudget.channelSplit,
  ].find((entry) => asArray(entry).length);
  const strategyRows = getStrategyAllocationRows(safePlan);
  if (structuredRows && !(strategyRows.length && isEstimatedEqualSplitFallback(structuredRows))) {
    return { rows: asArray(structuredRows), source: "structured_budget" };
  }

  if (strategyRows.length) {
    return { rows: strategyRows, source: "strategy_text" };
  }

  const candidates = [
    chartReady.budgetAllocation,
    chartReady.channelSplit,
    chartReady.platforms,
    safePlan.budgetAllocation,
    safePlan.mediaMix,
    safePlan.channelMix,
    safePlan.recommendations,
    safePlan.channels,
    safePlan.allocations,
    safePlan.platforms,
  ];
  const hit = candidates.find((entry) => asArray(entry).length);
  return { rows: asArray(hit), source: hit ? "structured_budget" : "missing" };
}

function getPlatformMetadataRows(plan = {}) {
  const safePlan = asObject(plan);
  const sections = asObject(safePlan.sections);
  const campaignDetails = asObject(
    safePlan.campaignDetails || sections.campaignDetails || sections.campaignDetailSection
  );
  const chartReady = asObject(safePlan.chartReady);
  return [
    ...asArray(campaignDetails.platformCards),
    ...asArray(chartReady.campaignBreakdown),
    ...asArray(safePlan.campaignBreakdown),
    ...asArray(safePlan.platformCards),
  ];
}

function getBenchmarkRows(plan = {}) {
  const safePlan = asObject(plan);
  const sections = asObject(safePlan.sections);
  const benchmark = asObject(safePlan.benchmarkJustification);
  const sectionBenchmark = asObject(sections.benchmarkJustification);
  const budget = asObject(safePlan.budgetArchitecture);
  const sectionBudget = asObject(sections.budgetArchitecture);
  const chartReady = asObject(safePlan.chartReady);
  return [
    ...asArray(benchmark.cards),
    ...asArray(benchmark.benchmarks),
    ...asArray(sectionBenchmark.cards),
    ...asArray(sectionBenchmark.benchmarks),
    ...asArray(budget.benchmarkCards),
    ...asArray(sectionBudget.benchmarkCards),
    ...asArray(chartReady.benchmarkCards),
    ...asArray(safePlan.benchmarkCards),
    ...asArray(safePlan.benchmarks),
  ];
}

function getEstimatedDeliveryRows(plan = {}) {
  const safePlan = asObject(plan);
  const sections = asObject(safePlan.sections);
  const budget = asObject(safePlan.budgetArchitecture);
  const sectionBudget = asObject(sections.budgetArchitecture);
  const chartReady = asObject(safePlan.chartReady);
  return [
    ...asArray(budget.estimatedDelivery),
    ...asArray(sectionBudget.estimatedDelivery),
    ...asArray(chartReady.estimatedDelivery),
    ...asArray(safePlan.estimatedDelivery),
    ...asArray(safePlan.deliveryEstimates),
  ];
}

function mergePlatformMetadata(rows = [], plan = {}, gates = resolvePresentationGates(plan)) {
  const metadataByKey = new Map();
  const benchmarkByKey = new Map();
  const deliveryByKey = new Map();
  getPlatformMetadataRows(plan).forEach((row) => {
    const key = getPlatformMatchKey(row);
    if (key && !metadataByKey.has(key)) metadataByKey.set(key, row);
  });
  getBenchmarkRows(plan).forEach((row) => {
    const benchmark = normalizeBenchmarkRow(row);
    if (!benchmark) return;
    benchmarkByKey.set(benchmark.key, { ...(benchmarkByKey.get(benchmark.key) || {}), ...benchmark });
  });
  getEstimatedDeliveryRows(plan).forEach((row) => {
    const delivery = normalizeDeliveryRow(row);
    if (!delivery) return;
    deliveryByKey.set(delivery.key, { ...(deliveryByKey.get(delivery.key) || {}), ...delivery });
  });
  /* Monetary-basis labels ride the governed channelSplit rows; when the chart
     rows come from the requested allocation instead, carry them across. */
  const basisByKey = new Map();
  const safePlanForBasis = asObject(plan);
  [
    ...asArray(asObject(safePlanForBasis.budgetArchitecture).channelSplit),
    ...asArray(asObject(asObject(safePlanForBasis.sections).budgetArchitecture).channelSplit),
  ].forEach((row) => {
    const key = getPlatformMatchKey(row);
    if (!key || basisByKey.has(key)) return;
    const labels = {};
    if (row?.budgetBasisLabel) labels.budgetBasisLabel = row.budgetBasisLabel;
    if (row?.estimatedImpressionsBasisLabel) labels.estimatedImpressionsBasisLabel = row.estimatedImpressionsBasisLabel;
    /* An APPROVED-forecast row's volume rides along: the backend's integer
       forecast (net working media ÷ net CPM) beats a re-derivation from the
       displayed, cent-rounded CPM. Planning-benchmark rows keep the existing
       recompute-from-CPM behaviour. */
    const approvedForecastRow =
      Boolean(row?.forecastAuthority) || /^Approved forecast/i.test(String(row?.benchmarkCpmLabel || ""));
    if (approvedForecastRow && row?.estimatedImpressions != null && row.estimatedImpressions !== "") {
      labels.estimatedImpressions = row.estimatedImpressions;
    }
    /* Presentation contract v2: the governed row's display values are the one
       canonical set of figures every surface shows. */
    if (gates.canonicalDeliveryFigures) {
      if (row?.benchmarkCpmDisplayValue != null && row.benchmarkCpmDisplayValue !== "") {
        /* As the backend's benchmark cards print it: "$31" for a whole-dollar
           directional benchmark, cents only when the published value has them. */
        const canonicalCpm = Number(row.benchmarkCpmDisplayValue);
        labels.canonicalCpmDisplayValue = Number.isFinite(canonicalCpm)
          ? Number.isInteger(canonicalCpm)
            ? `$${canonicalCpm}`
            : formatCurrency(canonicalCpm)
          : String(row.benchmarkCpmDisplayValue);
      }
      if (row?.estimatedImpressionsDisplayValue != null && row.estimatedImpressionsDisplayValue !== "") {
        labels.canonicalImpressionsDisplayValue = row.estimatedImpressionsDisplayValue;
      }
    }
    if (Object.keys(labels).length) basisByKey.set(key, labels);
  });

  return asArray(rows).map((row) => {
    const key = getPlatformMatchKey(row);
    const metadata = metadataByKey.get(key) || {};
    const benchmark = benchmarkByKey.get(key) || {};
    const delivery = deliveryByKey.get(key) || {};
    const basis = basisByKey.get(key) || {};
    return {
      ...metadata,
      ...benchmark,
      ...delivery,
      ...basis,
      ...row,
      canonicalDeliveryFigures: gates.canonicalDeliveryFigures,
      cpmRange: firstBenchmarkRate(
        gates.canonicalDeliveryFigures ? basis?.canonicalCpmDisplayValue ?? row?.benchmarkCpmDisplayValue : undefined,
        row?.cpmRange,
        row?.benchmarkCpm,
        row?.benchmarkCPM,
        row?.billedCpm,
        row?.billedCPM,
        row?.cpm,
        row?.estimatedCpm,
        row?.estCpm,
        metadata?.cpmRange,
        metadata?.benchmarkCpm,
        metadata?.benchmarkCPM,
        metadata?.billedCpm,
        metadata?.billedCPM,
        metadata?.cpm,
        metadata?.estimatedCpm,
        metadata?.estCpm,
        benchmark?.cpmRange
      ),
      cpcRange: firstBenchmarkRate(
        row?.cpcRange,
        row?.benchmarkCpc,
        row?.benchmarkCPC,
        row?.billedCpc,
        row?.billedCPC,
        row?.cpc,
        row?.estimatedCpc,
        row?.estCpc,
        metadata?.cpcRange,
        metadata?.benchmarkCpc,
        metadata?.benchmarkCPC,
        metadata?.billedCpc,
        metadata?.billedCPC,
        metadata?.cpc,
        metadata?.estimatedCpc,
        metadata?.estCpc,
        benchmark?.cpcRange
      ),
      cppRange: firstBenchmarkRate(
        row?.cppRange,
        row?.benchmarkCpp,
        row?.benchmarkCPP,
        row?.billedCpp,
        row?.billedCPP,
        row?.cpp,
        metadata?.cppRange,
        metadata?.benchmarkCpp,
        metadata?.benchmarkCPP,
        metadata?.billedCpp,
        metadata?.billedCPP,
        metadata?.cpp,
        benchmark?.cppRange
      ),
      benchmarkSourceLabel:
        getBenchmarkSourceLabel(row) ||
        getBenchmarkSourceLabel(metadata) ||
        benchmark?.benchmarkSourceLabel ||
        null,
      primaryObjective: resolvePlatformPrimaryObjective(row, metadata, gates),
      objective: firstPresent(row?.objective, metadata?.objective, metadata?.role),
      role: firstPresent(row?.role, metadata?.role),
      funnelPurpose: firstPresent(row?.funnelPurpose, metadata?.funnelPurpose),
      estimatedImpressions: firstPresent(
        gates.canonicalDeliveryFigures ? basis?.canonicalImpressionsDisplayValue : undefined,
        /* The backend's published display precision is the canonical figure
           (a directional volume held to three significant figures): the
           microsite's Impression Outlook and Funnel Architecture show the same
           311,000, never this chart's 311,102 beside it. */
        row?.estimatedImpressionsDisplayValue,
        row?.estimatedImpressions,
        row?.impressions,
        row?.estimated_impressions,
        /* The governed forecast volume (channelSplit / estimatedDelivery) beats
           any re-derivation from a displayed, cent-rounded CPM. */
        basis?.estimatedImpressions,
        metadata?.estimatedImpressionsDisplayValue,
        metadata?.estimatedImpressions,
        metadata?.impressions,
        metadata?.estimated_impressions
      ),
      estimatedClicks: firstPresent(
        row?.estimatedClicks,
        row?.clicks,
        row?.estimated_clicks,
        metadata?.estimatedClicks,
        metadata?.clicks,
        metadata?.estimated_clicks
      ),
      deliveryEstimatedImpressions: delivery?.estimatedImpressions,
      deliveryEstimatedClicks: delivery?.estimatedClicks,
    };
  });
}

function platformColor(name) {
  const key = normalizeKey(name);
  if (key.includes("meta") || key.includes("facebook") || key.includes("instagram")) return "#2f6fff";
  if (key.includes("google") || key.includes("search")) return "#39c5a5";
  if (key.includes("programmatic") || key.includes("display")) return "#8f63e8";
  if (key.includes("youtube")) return "#f26b3a";
  if (key.includes("tiktok")) return "#df5f7a";
  if (key.includes("ctv") || key.includes("ott")) return "#42c8f5";
  if (key.includes("audio")) return "#f0c04a";
  if (key.includes("dooh")) return "#55d07f";
  const identity = getPlatformMatchKey(name) || normalizeKey(name) || "platform";
  const hash = Array.from(identity).reduce(
    (total, character) => ((total * 31) + character.charCodeAt(0)) >>> 0,
    0
  );
  return FALLBACK_COLORS[hash % FALLBACK_COLORS.length];
}

function resolveFunnelStage(platform, { retargetingActive = undefined } = {}) {
  const stage = resolveFunnelStageFromSignals(platform);
  /* Presentation contract v2: a plan that activates no retargeting has no
     retargeting stage; that budget is mid-funnel work the approved lanes do. */
  if (retargetingActive === false && stage === "Retargeting") return "Consideration / Engagement";
  return stage;
}

function resolveFunnelStageFromSignals(platform) {
  const platformKey = getPlatformMatchKey(platform);
  const source = [
    platform?.key,
    platform?.platform,
    platform?.platformName,
    platform?.name,
    platform?.label,
    platform?.title,
    platform?.channel,
    platform?.primaryObjective,
    platform?.objective,
    platform?.campaignObjective,
    platform?.tactic,
    platform?.role,
    platform?.funnelPurpose,
    platform?.description,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  const hasAny = (signals) => signals.some((signal) => source.includes(signal));
  const hasAwareness = hasAny(AWARENESS_SIGNALS);
  const hasConsideration = hasAny(CONSIDERATION_SIGNALS);
  const hasRetargeting = hasAny(RETARGETING_SIGNALS);
  const hasConversion = hasAny(CONVERSION_SIGNALS);

  if (platformKey === "display" && hasRetargeting) return "Retargeting";
  if (platformKey === "google_search" && hasConversion) return "Conversion + Ticketing";
  if ((platformKey === "ctv" || platformKey === "dooh") && (hasAwareness || !hasConversion)) return "Awareness";
  if (platformKey === "youtube" && (hasConsideration || hasAwareness) && !hasConversion) {
    return hasConsideration ? "Consideration / Engagement" : "Awareness";
  }
  if (platformKey === "tiktok" && (hasConsideration || hasAwareness) && !hasConversion) {
    return hasConsideration ? "Consideration / Engagement" : "Awareness";
  }
  if (hasRetargeting) return "Retargeting";
  if (hasAwareness) return "Awareness";
  if (hasConsideration) return "Consideration / Engagement";
  if (hasConversion) return "Conversion + Ticketing";
  if (platformKey === "google_search") return "Conversion + Ticketing";
  if (platformKey === "display") return "Retargeting";
  if (platformKey === "youtube" || platformKey === "tiktok") return "Consideration / Engagement";
  return "Awareness";
}

function normalizePlatformRow(row, index, totalBudget, gates = {}) {
  const key = getPlatformMatchKey(row);
  const name = String(
    firstPresent(
      row?.name,
      row?.platform,
      row?.platformName,
      row?.platform_name,
      row?.label,
      row?.title,
      row?.channel,
      row?.key,
      row?.platformKey,
      row?.platform_key
    ) || `Platform ${index + 1}`
  ).trim();

  const percent = resolvePlatformAllocationPercent(row, {
    budgetBasis: totalBudget,
    totalBudget,
  });
  const hasExplicitPercent = Number.isFinite(percent);
  // Resolve dollars consistently: explicit dollar field → percent × basis → generic field.
  // A bare `budget`/`value` that actually holds a share/weight must never be rendered as a
  // tiny dollar amount, so the percentage basis wins over those ambiguous fields.
  const budget = resolvePlatformBudgetAmount(
    { ...row, value: hasExplicitPercent ? undefined : row?.value },
    { budgetBasis: totalBudget, totalBudget, percent: hasExplicitPercent ? percent : null }
  );

  if (!(budget > 0) && !(Number.isFinite(percent) && percent > 0)) return null;

  const cpmRange = firstBenchmarkRate(
    row?.cpmRange,
    row?.benchmarkCpm,
    row?.benchmarkCPM,
    row?.billedCpm,
    row?.billedCPM,
    row?.cpm,
    row?.estimatedCpm,
    row?.estCpm
  );
  const cpcRange = firstBenchmarkRate(
    row?.cpcRange,
    row?.benchmarkCpc,
    row?.benchmarkCPC,
    row?.billedCpc,
    row?.billedCPC,
    row?.cpc,
    row?.estimatedCpc,
    row?.estCpc
  );
  const cppRange = firstBenchmarkRate(
    row?.cppRange,
    row?.benchmarkCpp,
    row?.benchmarkCPP,
    row?.billedCpp,
    row?.billedCPP,
    row?.cpp
  );
  const cpm = parseRateValue(cpmRange);
  const cpc = parseRateValue(cpcRange);
  const formulaImpressions = budget > 0 && cpm ? (budget / cpm) * 1000 : null;
  const formulaClicks = budget > 0 && cpc ? budget / cpc : null;
  const incomingImpressions = firstPresent(
    row?.estimatedImpressions,
    row?.impressions,
    row?.estimated_impressions
  );
  const estimatedImpressions =
    cpc && !cpm
      ? null
      : row?.canonicalDeliveryFigures && incomingImpressions != null && incomingImpressions !== ""
        ? /* Presentation contract v2: the published figure is canonical; never
             re-derived from a rounded CPM. */
          incomingImpressions
        : resolveEstimatedDeliveryCount({
            incoming: incomingImpressions,
            formula: formulaImpressions,
            fallback: row?.deliveryEstimatedImpressions,
            budget,
            cpm,
          });
  const estimatedClicks =
    firstPresent(
      row?.estimatedClicks,
      row?.clicks,
      row?.estimated_clicks,
      formulaClicks,
      row?.deliveryEstimatedClicks
    );
  const primaryObjective = firstPresent(
    row?.primaryObjective,
    row?.objective,
    row?.campaignObjective,
    row?.tactic,
    row?.funnelPurpose,
    row?.role
  );
  const resolvedPrimaryObjective = isGenericPrimaryObjective(primaryObjective)
    ? getPlatformSpecificObjective({ ...row, name }, gates)
    : primaryObjective;
  const normalized = {
    key,
    name,
    budget: budget || 0,
    percent: Number.isFinite(percent) ? percent : 0,
    cpmRange: cpmRange ?? null,
    cpcRange: cpcRange ?? null,
    cppRange: cppRange ?? null,
    benchmarkSourceLabel: getBenchmarkSourceLabel(row),
    /* Monetary basis of the budget / estimate columns, backend-authored
       ("Client-billed", "Approved forecast · client-billed"); the portal
       never derives a basis. */
    budgetBasisLabel: firstPresent(row?.budgetBasisLabel, row?.budget_basis_label) || null,
    estimatedImpressionsBasisLabel:
      firstPresent(row?.estimatedImpressionsBasisLabel, row?.estimated_impressions_basis_label) || null,
    estimatedImpressions,
    estimatedClicks,
    primaryObjective: resolvedPrimaryObjective ? String(resolvedPrimaryObjective) : getPlatformSpecificObjective({ ...row, name }, gates),
    funnelStage: resolveFunnelStage({ ...row, primaryObjective: resolvedPrimaryObjective }, gates),
    color: platformColor(name),
    isEstimatedFallback: Boolean(row?.estimated),
  };
  return normalized;
}

export function normalizeMediaPlanChartData(plan = {}, { monetaryBasis = null } = {}) {
  const safePlan = asObject(plan);
  const sections = asObject(safePlan.sections);
  const chartReady = asObject(safePlan.chartReady);
  const budgetArchitecture = asObject(safePlan.budgetArchitecture);
  const sectionBudgetArchitecture = asObject(sections.budgetArchitecture);
  const financials = asObject(safePlan.financials || safePlan.financialSummary || safePlan.financial_summary);
  const grossTotal = parseBudgetValue(
    firstPresent(
      safePlan.totalBudget,
      safePlan.budget,
      safePlan.total_budget,
      chartReady.totalBudget,
      budgetArchitecture.totalBudget,
      sectionBudgetArchitecture.totalBudget
    )
  );
  const workingMediaBudget = parseBudgetValue(
    firstPresent(
      safePlan.netWorkingMediaBudget,
      safePlan.net_working_media_budget,
      safePlan.workingMediaBudget,
      safePlan.working_media_budget,
      safePlan.netMediaBudget,
      safePlan.net_media_budget,
      chartReady.netWorkingMediaBudget,
      chartReady.net_working_media_budget,
      chartReady.workingMediaBudget,
      chartReady.working_media_budget,
      chartReady.netMediaBudget,
      chartReady.net_media_budget,
      budgetArchitecture.netWorkingMediaBudget,
      budgetArchitecture.net_working_media_budget,
      budgetArchitecture.workingMediaBudget,
      budgetArchitecture.working_media_budget,
      budgetArchitecture.netMediaBudget,
      budgetArchitecture.net_media_budget,
      sectionBudgetArchitecture.netWorkingMediaBudget,
      sectionBudgetArchitecture.net_working_media_budget,
      sectionBudgetArchitecture.workingMediaBudget,
      sectionBudgetArchitecture.working_media_budget,
      sectionBudgetArchitecture.netMediaBudget,
      sectionBudgetArchitecture.net_media_budget,
      financials.netWorkingMediaBudget,
      financials.net_working_media_budget,
      financials.workingMediaBudget,
      financials.working_media_budget,
      financials.netMediaBudget,
      financials.net_media_budget
    )
  );
  // A microsite may carry private net media beside explicitly gross rows.
  // Its server-declared basis keeps those rows on their own denominator;
  // other planner callers retain their existing working-media basis.
  const explicitTotal = monetaryBasis === "GROSS_CLIENT_BILLED"
    ? grossTotal
    : workingMediaBudget || grossTotal;
  const gates = resolvePresentationGates(safePlan);
  const candidate = getCandidateRows(safePlan);
  const rows = mergePlatformMetadata(candidate.rows, safePlan, gates);
  const firstPass = rows
    .map((row, index) => normalizePlatformRow(row, index, explicitTotal, gates))
    .filter(Boolean);
  const rowBudgetTotal = firstPass.reduce((sum, row) => sum + (Number(row.budget) || 0), 0);
  const totalBudget = explicitTotal || rowBudgetTotal;

  const unsortedPlatforms = firstPass
    .map((row) => ({
      ...row,
      budget:
        row.budget ||
        (Number.isFinite(row.percent) && totalBudget ? (totalBudget * row.percent) / 100 : 0),
    }))
    .filter((row) => row.budget > 0)
    .map((row) => ({
      ...row,
      percent: totalBudget ? (row.budget / totalBudget) * 100 : row.percent,
    }));
  const hasCanonicalAllocation =
    candidate.source !== "missing" && !isEstimatedEqualSplitFallback(candidate.rows);
  const platforms = orderPlatformRowsByAllocation(
    unsortedPlatforms,
    hasCanonicalAllocation ? unsortedPlatforms : [],
    { fallbackComparator: compareChartPlatformRows }
  );

  const stageMap = new Map();
  platforms.forEach((platform) => {
    const name = platform.funnelStage || "Awareness";
    const existing = stageMap.get(name) || {
      name,
      budget: 0,
      percent: 0,
      color: STAGE_COLORS[name] || platform.color,
    };
    existing.budget += platform.budget;
    stageMap.set(name, existing);
  });
  const funnelStages = Array.from(stageMap.values()).map((stage) => ({
    ...stage,
    percent: totalBudget ? (stage.budget / totalBudget) * 100 : 0,
  })).sort((a, b) => {
    const delta = Number(b?.percent || 0) - Number(a?.percent || 0);
    if (delta !== 0) return delta;
    return String(a?.name || "").localeCompare(String(b?.name || ""));
  });

  return {
    totalBudget: totalBudget || 0,
    platforms,
    funnelStages,
    allocationSource: candidate.source,
    usedFallbackEqualSplit:
      candidate.source !== "strategy_text" && firstPass.some((row) => row.isEstimatedFallback),
  };
}

export function formatCurrency(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "N/A";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: numeric >= 1000 ? 0 : 2,
  }).format(numeric);
}

export function formatPercent(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "N/A";
  return `${Math.round(numeric)}%`;
}

/*
 * Counts render as actual figures ("311,000"), never abbreviations — the one
 * rule in utils/micrositeMetricFormat, shared with the strategy microsite.
 * A pre-formatted label string still passes through untouched.
 */
export function formatCompactNumber(value) {
  const formatted = formatMetricCountOrRange(value, "");
  if (formatted) return formatted;
  return typeof value === "string" && value.trim() ? value.trim() : "N/A";
}

export function formatMetricRange(value, fallbackLabel = "N/A") {
  if (value == null || value === "") return fallbackLabel;
  const raw = String(value).trim();
  if (!raw || /^(null|undefined|nan|\[object object\])$/i.test(raw)) return fallbackLabel;
  if (/\$/.test(raw)) return raw.replace(/\s+/g, " ");
  const budget = parseBudgetValue(raw);
  if (budget != null && !/[a-z]/i.test(raw.replace(/k|m/gi, ""))) return formatCurrency(budget);
  return raw;
}

export function formatBenchmarkPrice(platform = {}) {
  const rateRows = [
    ["CPM", platform?.cpmRange],
    ["CPC", platform?.cpcRange],
    ["CPP", platform?.cppRange],
  ];
  const row = rateRows.find(([, value]) => formatMetricRange(value, ""));
  if (!row) return "No benchmark price";

  const [unit, value] = row;
  const formatted = formatMetricRange(value, "");
  if (!formatted) return "No benchmark price";
  return new RegExp(`\\b${unit}\\b`, "i").test(formatted)
    ? formatted
    : `${formatted} ${unit}`;
}

function sanitizeDeliveryValue(value) {
  if (value == null || value === "") return null;
  if (typeof value === "number") return Math.abs(value);
  const raw = String(value).trim();
  if (!raw || /^(null|undefined|nan|\[object object\])$/i.test(raw)) return null;
  return raw
    .replace(/^[~≈]\s*/u, "")
    .replace(/^[-−]\s*/u, "")
    .replace(/\s*(?:impressions?|clicks?)\s*$/i, "")
    .trim();
}

function isClickDeliveryMode(platform = {}) {
  // Data-driven: a row shows clicks only when its benchmark is CPC (a cpcRange
  // with no cpmRange) or when only clicks were estimated. Never hard-code a
  // platform (e.g. Google Search) to clicks — a CPM benchmark on any platform
  // must render impressions (the "641 clicks" bug came from forcing search to
  // clicks even when its benchmark was CPM).
  if (platform?.cpcRange && !platform?.cpmRange) return true;
  const hasImpressions =
    platform?.estimatedImpressions != null && platform.estimatedImpressions !== "";
  const hasClicks = platform?.estimatedClicks != null && platform.estimatedClicks !== "";
  return hasClicks && !hasImpressions;
}

function formatDeliveryMetricValue(value, label) {
  const cleaned = sanitizeDeliveryValue(value);
  if (cleaned == null || cleaned === "") return "N/A";
  return `${formatCompactNumber(cleaned)} ${label}`;
}

export function formatImpressionsOrClicks(platform) {
  if (isClickDeliveryMode(platform)) {
    return formatDeliveryMetricValue(platform?.estimatedClicks, "Clicks");
  }
  if (platform?.estimatedImpressions != null && platform.estimatedImpressions !== "") {
    return formatDeliveryMetricValue(platform.estimatedImpressions, "Impressions");
  }
  if (platform?.estimatedClicks != null && platform.estimatedClicks !== "") {
    return formatDeliveryMetricValue(platform.estimatedClicks, "Clicks");
  }
  return "N/A";
}

export function formatPrimaryObjective(value, fallback = "Awareness") {
  const raw = String(firstPresent(value, fallback) || "").trim();
  const cleaned = raw
    .replace(/^(?:[-*]\s*)?primary\s+role\s*:\s*/i, "")
    .trim();
  const objective = cleaned || String(fallback || "Awareness").trim();
  const punctuated = /[.!?]$/.test(objective) ? objective : `${objective}.`;
  return `- PRIMARY ROLE: ${punctuated}`;
}

export function buildPlatformTooltipRows(item = {}) {
  /* A projected datum (proposal mode) carries its own basis-labelled rows. */
  if (Array.isArray(item.tooltipRows)) return item.tooltipRows.filter(([label, value]) => label && value);
  const rows = [];
  const budget = parseBudgetValue(item.budget);
  const allocation = parsePercentValue(item.percent);
  if (budget != null) rows.push(["Budget", formatCurrency(budget)]);
  if (allocation != null) rows.push(["Allocation", formatPercent(allocation)]);
  const delivery = formatImpressionsOrClicks(item);
  if (delivery !== "N/A") rows.push(["Est. Delivery", delivery]);
  const cpm = formatMetricRange(item.cpmRange, "");
  const cpc = formatMetricRange(item.cpcRange, "");
  const cpp = formatMetricRange(item.cppRange, "");
  if (cpm) rows.push(["CPM", cpm]);
  if (cpc) rows.push(["CPC", cpc]);
  if (cpp) rows.push(["CPP", cpp]);
  const role = firstPresent(item.primaryObjective, item.objective, item.funnelStage);
  if (role) rows.push(["Role", String(role).replace(/^(?:[-*]\s*)?primary\s+role\s*:\s*/i, "")]);
  return rows;
}

export function resolveChartTooltipPlatformBranding(item) {
  return resolveMicrositePlatformBranding(item) || PLATFORM_FALLBACK_BRANDING;
}

export function ChartPopover({
  item = {},
  labelKey = "name",
  className = "",
  style = undefined,
  showPlatformLogo = true,
  id = undefined,
}) {
  const brand = showPlatformLogo ? resolveChartTooltipPlatformBranding(item) : null;
  const title = item?.[labelKey] || item?.name || item?.label || item?.platform || "Platform";

  return (
    <div id={id} className={`sherloc-plan-chart-popover ${className}`.trim()} role="tooltip" style={style}>
      <strong>
        {brand?.image ? (
          <span className="sherloc-plan-chart-popover-logo" aria-hidden="true">
            <img src={brand.image} alt="" />
          </span>
        ) : null}
        <span className="sherloc-plan-chart-popover-title-text">{title}</span>
      </strong>
      <dl>
        {buildPlatformTooltipRows(item).map(([label, value]) => (
          <div key={label}>
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function getChartItemAriaLabel(item = {}, labelKey = "name") {
  const details = buildPlatformTooltipRows(item)
    .map(([label, value]) => `${label}: ${value}`)
    .join(", ");
  return `${item[labelKey]} details${details ? `, ${details}` : ""}`;
}

export function calculateChartPopoverPosition(
  anchorRect,
  viewport = {},
  popoverSize = POPOVER_SIZE
) {
  if (!anchorRect) return { top: POPOVER_VIEWPORT_PADDING, left: POPOVER_VIEWPORT_PADDING, placement: "below" };
  const viewportWidth = Number(viewport.width) || 1024;
  const viewportHeight = Number(viewport.height) || 768;
  const scrollX = Number(viewport.scrollX) || 0;
  const scrollY = Number(viewport.scrollY) || 0;
  const width = Number(popoverSize.width) || POPOVER_SIZE.width;
  const height = Number(popoverSize.height) || POPOVER_SIZE.height;
  const minLeft = scrollX + POPOVER_VIEWPORT_PADDING;
  const maxLeft = scrollX + viewportWidth - width - POPOVER_VIEWPORT_PADDING;
  const centeredLeft = scrollX + anchorRect.left + anchorRect.width / 2 - width / 2;
  const left = Math.min(Math.max(centeredLeft, minLeft), Math.max(minLeft, maxLeft));
  const belowTop = scrollY + anchorRect.bottom + POPOVER_GAP;
  const aboveTop = scrollY + anchorRect.top - height - POPOVER_GAP;
  const fitsBelow = belowTop + height <= scrollY + viewportHeight - POPOVER_VIEWPORT_PADDING;
  const fitsAbove = aboveTop >= scrollY + POPOVER_VIEWPORT_PADDING;
  const placement = fitsBelow || !fitsAbove ? "below" : "above";
  const unclampedTop = placement === "below" ? belowTop : aboveTop;
  const minTop = scrollY + POPOVER_VIEWPORT_PADDING;
  const maxTop = scrollY + viewportHeight - height - POPOVER_VIEWPORT_PADDING;
  const top = Math.min(Math.max(unclampedTop, minTop), Math.max(minTop, maxTop));
  return { top, left, placement };
}

function getAnchorRect(target) {
  const rect = target?.getBoundingClientRect?.();
  if (rect) return rect;
  return {
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
    width: 0,
    height: 0,
  };
}

function getBarAnchorRect(target) {
  const targetElement = target?.querySelector?.(".sherloc-plan-bar-fill") || target;
  return getAnchorRect(targetElement);
}

export function ChartPopoverPortal({ popover }) {
  if (!popover || typeof document === "undefined") return null;
  const position = calculateChartPopoverPosition(popover.anchorRect, {
    width: window.innerWidth,
    height: window.innerHeight,
    scrollX: window.scrollX,
    scrollY: window.scrollY,
  });
  return createPortal(
    <ChartPopover
      item={popover.item}
      labelKey={popover.labelKey}
      showPlatformLogo={popover.showPlatformLogo}
      className={`sherloc-plan-chart-popover--portal sherloc-plan-chart-popover--${position.placement}`}
      style={{ top: position.top, left: position.left }}
      id={popover.tooltipId}
    />,
    document.body
  );
}

export function getChartInteractionProps(
  item,
  labelKey,
  showPopover,
  hidePopover,
  getRect = getAnchorRect,
  options = {}
) {
  const show = (event) =>
    showPopover(
      item,
      labelKey,
      getRect(event.currentTarget),
      options.showPlatformLogo !== false,
      options.tooltipId
    );
  return {
    tabIndex: 0,
    role: "button",
    "aria-label": options.ariaLabel || getChartItemAriaLabel(item, labelKey),
    "aria-describedby": options.tooltipId,
    "data-platform-tooltip-trigger": "true",
    onMouseEnter: show,
    onMouseMove: show,
    onFocus: show,
    onClick: show,
    onMouseLeave: hidePopover,
    onBlur: hidePopover,
    onKeyDown: (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        hidePopover();
      }
    },
  };
}

export function useAnchoredChartPopover() {
  const [activePopover, setActivePopover] = useState(null);
  const showPopover = useCallback(
    (item, labelKey = "name", anchorRect = null, showPlatformLogo = true, tooltipId = undefined) => {
      setActivePopover({ item, labelKey, anchorRect, showPlatformLogo, tooltipId });
    },
    []
  );
  const hidePopover = useCallback(() => setActivePopover(null), []);

  useEffect(() => {
    if (!activePopover || typeof document === "undefined") return undefined;
    const handlePointerDown = (event) => {
      if (event.target?.closest?.('[data-platform-tooltip-trigger="true"]')) return;
      hidePopover();
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") hidePopover();
    };
    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [activePopover, hidePopover]);

  return { activePopover, showPopover, hidePopover };
}

function getChartDisplayPlatformName(name) {
  const key = normalizePlatformAllocationKey(name, { allowUnknown: true });
  if (key === "meta") return "Meta";
  if (key === "display") return "Display";
  return name;
}

function DonutChart({
  data,
  valueKey = "budget",
  labelKey = "name",
  showPopover,
  hidePopover,
  showPlatformLogos = true,
  centerLabel = "planned",
}) {
  const total = data.reduce((sum, item) => sum + Number(item[valueKey] || 0), 0);
  const centerLines = Array.isArray(centerLabel) ? centerLabel : [centerLabel];
  let offset = 25;
  return (
    <div className="sherloc-plan-donut-wrap" aria-hidden={total > 0 ? "false" : "true"}>
      <svg viewBox="0 0 120 120" className="sherloc-plan-donut">
        <circle className="sherloc-plan-donut-track" cx="60" cy="60" r="42" />
        {data.map((item) => {
          const value = Number(item[valueKey] || 0);
          const dash = total ? (value / total) * 263.89 : 0;
          const segment = (
            <g
              key={item[labelKey]}
              className="sherloc-plan-donut-segment-item"
              {...getChartInteractionProps(item, labelKey, showPopover, hidePopover, getAnchorRect, {
                showPlatformLogo: showPlatformLogos,
              })}
            >
              <circle
                className="sherloc-plan-donut-segment"
                cx="60"
                cy="60"
                r="42"
                stroke={item.color}
                strokeDasharray={`${dash} ${263.89 - dash}`}
                strokeDashoffset={-offset}
              >
                <title>{`${item[labelKey]}: ${formatPercent(item.percent)} · ${formatCurrency(item.budget)}`}</title>
              </circle>
            </g>
          );
          offset += dash;
          return segment;
        })}
        <text x="60" y={centerLines.length > 1 ? 54 : 57} className="sherloc-plan-donut-center-main">
          {formatCurrency(total)}
        </text>
        {centerLines.map((line, index) => (
          <text
            key={line}
            x="60"
            y={(centerLines.length > 1 ? 65 : 69) + index * 7}
            className="sherloc-plan-donut-center-sub"
          >
            {line}
          </text>
        ))}
      </svg>
    </div>
  );
}

function Legend({ data, labelKey = "name" }) {
  return (
    <div className="sherloc-plan-chart-legend">
      {data.map((item) => (
        <span key={item[labelKey]} className="sherloc-plan-legend-item">
          <i style={{ backgroundColor: item.color }} />
          <span>{getChartDisplayPlatformName(item[labelKey])}</span>
          <strong>{formatPercent(item.percent)}</strong>
        </span>
      ))}
    </div>
  );
}

function BarChart({ platforms, showPopover, hidePopover }) {
  const max = Math.max(...platforms.map((platform) => platform.budget), 1);
  return (
    <div className="sherloc-plan-bar-chart">
      {platforms.map((platform) => {
        const brand = resolveSherlocPlatformBranding(platform) || PLATFORM_FALLBACK_BRANDING;
        return (
          <div
            key={platform.name}
            className="sherloc-plan-bar-column"
            {...getChartInteractionProps(platform, "name", showPopover, hidePopover, getBarAnchorRect, {
              showPlatformLogo: true,
            })}
          >
            <div className="sherloc-plan-bar-frame">
              <div
                className="sherloc-plan-bar-fill"
                style={{
                  height: `${Math.max(8, (platform.budget / max) * 100)}%`,
                  backgroundColor: platform.color,
                }}
              >
                <span>{formatCurrency(platform.budget)}</span>
              </div>
            </div>
            <span className="sherloc-plan-bar-logo-label" title={brand.label}>
              <img src={brand.image} alt={brand.label} />
            </span>
            <span className="sherloc-plan-bar-text-stack">
              <span className="sherloc-plan-bar-platform-name">{getChartDisplayPlatformName(platform.name)}</span>
              <span className="sherloc-plan-bar-sub">{formatImpressionsOrClicks(platform)}</span>
            </span>
          </div>
        );
      })}
    </div>
  );
}

/*
 * A `projection` (proposal mode — CampaignProposalPlanning
 * buildProposalMediaPlanChartProjection) replaces the planner normalization
 * with rows already on a named basis. Same cards, geometry and interactions;
 * each card states its basis, and Platform Detail lists one row per planning
 * case.
 */
const applyProjectionColors = (projection) => {
  const platforms = asArray(projection.platforms).map((platform) => ({
    ...platform,
    color: platform.color || platformColor(platform.name),
  }));
  const funnelStages = asArray(projection.funnelStages).map((stage) => ({
    ...stage,
    color:
      stage.color ||
      STAGE_COLORS[stage.name] ||
      STAGE_COLORS[String(stage.name || "").split(" / ")[0]] ||
      platforms[0]?.color,
  }));
  return { ...projection, totalBudget: Number(projection.totalBudget) || 0, platforms, funnelStages };
};

function ChartBasis({ lines = [] }) {
  const entries = lines.filter(Boolean);
  if (!entries.length) return null;
  return (
    <div className="sherloc-plan-chart-basis">
      {entries.map((line) => (
        <p key={line}>{line}</p>
      ))}
    </div>
  );
}

function ProjectedPlatformDetailTable({ platforms, detail }) {
  const color = platforms[0]?.color;
  return (
    <>
      <div className="sherloc-plan-table-wrap">
        <table className="sherloc-plan-table sherloc-plan-table--proposal-detail">
          <thead>
            <tr>
              <th scope="col">Platform / Planning Case</th>
              <th scope="col">Client Investment</th>
              <th scope="col">Working CPM</th>
              <th scope="col">Client CPM</th>
              <th scope="col">Forecast / Est. Impressions</th>
              <th scope="col">Primary Objective</th>
            </tr>
          </thead>
          <tbody>
            {asArray(detail.rows).map((row) => (
              <tr key={row.key} data-planning-case={row.key}>
                <td data-label="Platform / Planning Case">
                  <span className="sherloc-plan-rate-cell">
                    <span className="sherloc-plan-platform-pill" style={{ "--sherloc-plan-platform-color": color }}>
                      {row.platform}
                    </span>
                    <span className="sherloc-plan-rate-primary">{row.caseLabel}</span>
                    <span className="sherloc-plan-rate-secondary">{row.caseNote}</span>
                  </span>
                </td>
                <td data-label="Client Investment">
                  <span className="sherloc-plan-rate-cell">
                    <span className="sherloc-plan-rate-primary">{row.investment}</span>
                    <span className="sherloc-plan-rate-secondary">{row.investmentNote}</span>
                  </span>
                </td>
                <td data-label="Working CPM">
                  <span className="sherloc-plan-rate-cell">
                    <span className="sherloc-plan-rate-primary">{row.workingCpm}</span>
                    <span className="sherloc-plan-rate-secondary">{row.workingCpmNote}</span>
                  </span>
                </td>
                <td data-label="Client CPM">
                  <span className="sherloc-plan-rate-cell">
                    <span className="sherloc-plan-rate-primary">{row.clientCpm}</span>
                    <span className="sherloc-plan-rate-secondary">{row.clientCpmNote}</span>
                  </span>
                </td>
                <td data-label="Forecast / Est. Impressions">
                  <span className="sherloc-plan-rate-cell">
                    <span className="sherloc-plan-rate-primary">{row.impressions}</span>
                    <span className="sherloc-plan-rate-secondary">{row.impressionsNote}</span>
                  </span>
                </td>
                <td data-label="Primary Objective">{row.objective}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ChartBasis lines={[detail.footnote]} />
    </>
  );
}

export default function SherlocMediaPlanCharts({ plan, projection = null, monetaryBasis = null }) {
  const { activePopover, showPopover, hidePopover } = useAnchoredChartPopover();
  const normalized = useMemo(
    () => (projection ? applyProjectionColors(projection) : normalizeMediaPlanChartData(plan, { monetaryBasis })),
    [plan, projection, monetaryBasis]
  );
  const { platforms, funnelStages } = normalized;
  const hasPlatformBudget = platforms.length > 0 && normalized.totalBudget > 0;
  const hasMonthly = Boolean(plan?.monthlyPacing || plan?.monthlySpend || plan?.chartReady?.monthlyPacing);

  if (!hasPlatformBudget) {
    return (
      <section className="sherloc-plan-charts-section">
        <div className="sherloc-plan-chart-card sherloc-plan-chart-empty">
          Media mix visualization will appear once platform budget allocations are available.
        </div>
      </section>
    );
  }

  return (
    <section
      className={`sherloc-plan-charts-section${projection ? " sherloc-plan-charts-section--projected" : ""}`}
      aria-label="Media plan visual strategy"
    >
      <ChartPopoverPortal popover={activePopover} />
      <div className="sherloc-plan-chart-grid">
        <article className="sherloc-plan-chart-card">
          <h3>Budget Allocation by Platform</h3>
          {projection ? <ChartBasis lines={[normalized.budgetBasis, normalized.budgetSecondaryBasis]} /> : null}
          <Legend data={platforms} />
          <DonutChart
            data={platforms}
            showPopover={showPopover}
            hidePopover={hidePopover}
            centerLabel={normalized.centerLabel}
          />
        </article>

        {funnelStages.length ? (
          <article className="sherloc-plan-chart-card">
            <h3>Funnel Stage Breakdown — Spend vs. Objective</h3>
            {projection ? <ChartBasis lines={[normalized.funnelBasis]} /> : null}
            <Legend data={funnelStages} labelKey="name" />
            <DonutChart
              data={funnelStages}
              showPopover={showPopover}
              hidePopover={hidePopover}
              showPlatformLogos={false}
              centerLabel={normalized.centerLabel}
            />
          </article>
        ) : null}
      </div>

      <article className="sherloc-plan-chart-card sherloc-plan-chart-card--wide">
        <h3>
          {hasMonthly && !projection
            ? "Monthly Spend by Platform — With Estimated Impressions"
            : "Flight Spend by Platform — With Estimated Impressions"}
        </h3>
        {projection ? <ChartBasis lines={[normalized.spendBasis]} /> : null}
        <BarChart platforms={platforms} showPopover={showPopover} hidePopover={hidePopover} />
        {projection ? <ChartBasis lines={[normalized.deliveryNote]} /> : null}
      </article>

      <article className="sherloc-plan-chart-card sherloc-plan-chart-card--wide">
        <h3>Platform Detail</h3>
        {projection?.detail ? (
          <ProjectedPlatformDetailTable platforms={platforms} detail={projection.detail} />
        ) : (
          <div className="sherloc-plan-table-wrap">
            <table className="sherloc-plan-table sherloc-plan-table--platform-detail">
              <colgroup>
                <col className="sherloc-plan-table-col--platform" />
                <col className="sherloc-plan-table-col--budget" />
                <col className="sherloc-plan-table-col--rate" />
                <col className="sherloc-plan-table-col--delivery" />
                <col className="sherloc-plan-table-col--objective" />
              </colgroup>
              <thead>
                <tr>
                  <th>Platform</th>
                  <th>Flight Budget</th>
                  <th>Est. CPM / CPC</th>
                  <th>Est. Impressions / Clicks</th>
                  <th>Primary Objective</th>
                </tr>
              </thead>
              <tbody>
                {platforms.map((platform) => {
                  const platformDisplayName = getChartDisplayPlatformName(platform.name);
                  return (
                    <tr key={platform.name}>
                      <td>
                        <span
                          className="sherloc-plan-platform-pill"
                          style={{ "--sherloc-plan-platform-color": platform.color }}
                        >
                          {platformDisplayName}
                        </span>
                      </td>
                      <td>
                        <span className="sherloc-plan-rate-cell">
                          <span className="sherloc-plan-rate-primary">{formatCurrency(platform.budget)}</span>
                          {platform.budgetBasisLabel ? (
                            <span className="sherloc-plan-rate-secondary">{platform.budgetBasisLabel}</span>
                          ) : null}
                        </span>
                      </td>
                      <td>
                        <span className="sherloc-plan-rate-cell">
                          <span className="sherloc-plan-rate-primary">
                            {formatBenchmarkPrice(platform)}
                          </span>
                          {platform.benchmarkSourceLabel ? (
                            <span className="sherloc-plan-rate-secondary">
                              {platform.benchmarkSourceLabel}
                            </span>
                          ) : null}
                        </span>
                      </td>
                      <td>{formatImpressionsOrClicks(platform)}</td>
                      <td>{formatPrimaryObjective(platform.primaryObjective || platform.funnelStage)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </article>
    </section>
  );
}
